import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { AccrualMatchAuditSnapshot } from "@common/schemas/domains/accrualReview/accrual-match-audit-log.schema";
import { AccrualMatchTable } from "@components/accounting/transactionMatch/AccrualMatchTable";
import { AccrualMatchHistoryAction } from "@components/auditLog/accrual/AccrualMatchHistoryAction";
import {
    mapSnapshotToItemsMatchableItems,
    mapSnapshotToSourceMatchableItem,
} from "./mapSnapshotToMatchableItems";
import styles from "./HistoryAccrualMatchDetails.module.scss";

interface Props {
    snapshot: AccrualMatchAuditSnapshot;
    isMatched: boolean;
    sourceId?: string | null;
    isReverted?: boolean;
    onAfterRevert?: () => void;
}

export const AccrualMatchCard: React.FC<Props> = ({
    snapshot,
    isMatched,
    sourceId,
    isReverted,
    onAfterRevert,
}) => {
    const sourceItem = useMemo(
        () => mapSnapshotToSourceMatchableItem(snapshot),
        [snapshot],
    );
    const matchedItems = useMemo(
        () => mapSnapshotToItemsMatchableItems(snapshot),
        [snapshot],
    );

    const data = useMemo(
        () => [sourceItem, ...matchedItems],
        [sourceItem, matchedItems],
    );

    const cardRef = useRef<HTMLDivElement | null>(null);
    const [overlayTop, setOverlayTop] = useState<number | null>(null);

    useLayoutEffect(() => {
        const cardEl = cardRef.current;
        if (!cardEl) {
            return;
        }
        const measure = () => {
            const row = cardEl.querySelector('[data-row-index="1"]');
            if (row instanceof HTMLElement) {
                setOverlayTop(row.offsetTop);
            }
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(cardEl);
        return () => observer.disconnect();
    }, [data]);

    return (
        <div ref={cardRef} className={styles.card}>
            <AccrualMatchTable data={data} section="source" />
            {overlayTop !== null ? (
                <AccrualMatchHistoryAction
                    snapshot={snapshot}
                    matched={isMatched}
                    sourceId={sourceId ?? null}
                    isReverted={isReverted ?? false}
                    onAfterRevert={onAfterRevert}
                    className={styles.matchActionOverlay}
                    style={{ top: overlayTop }}
                />
            ) : null}
        </div>
    );
};
